export type Currency = 'USD' | 'INR';

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  INR: '₹',
};

export const CURRENCY_CODES: Record<Currency, string> = {
  USD: 'USD',
  INR: 'INR',
};

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: Currency = 'INR'): string {
  return CURRENCY_SYMBOLS[currency] || '₹';
}

/**
 * Format a numeric string with commas (no parseFloat - preserves e.g. 55999999)
 */
function formatWithCommas(valueStr: string, showDecimals = false): string {
  const cleaned = valueStr.replace(/,/g, '').trim();
  const parts = cleaned.split('.');
  const intPart = (parts[0] || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decPart = parts[1];
  if (showDecimals && decPart !== undefined) return `${intPart}.${decPart}`;
  if (showDecimals && decPart === undefined) return `${intPart}.00`;
  return intPart;
}

/**
 * Format currency value in Indian Rupee (INR) only.
 * Standard: ₹ symbol, Indian units K (thousands), L (Lakhs), Cr (Crores).
 * All amounts are shown as INR regardless of stored currency.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  _currency?: Currency,
  options?: {
    showDecimals?: boolean;
    compact?: boolean;
  }
): string {
  const symbol = '₹';
  if (value === null || value === undefined) return `${symbol}0`;

  const strVal = typeof value === 'string' ? value.replace(/,/g, '').trim() : String(value);
  if (!strVal || strVal === '') return `${symbol}0`;
  const numValue = parseFloat(strVal);
  if (isNaN(numValue)) return `${symbol}0`;

  const showDecimals = options?.showDecimals ?? false;
  const compact = options?.compact ?? false;

  if (compact && numValue >= 1000) {
    if (numValue >= 10000000) return `${symbol}${(numValue / 10000000).toFixed(1)}Cr`;
    if (numValue >= 100000) return `${symbol}${(numValue / 100000).toFixed(1)}L`;
    return `${symbol}${(numValue / 1000).toFixed(0)}K`;
  }

  const formatted = formatWithCommas(strVal, showDecimals && strVal.includes('.'));
  return `${symbol}${formatted}`;
}

/**
 * Round to 2 decimal places to avoid float precision (e.g. 31000 showing as 30999.00)
 */
export function roundMoney(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Parse currency value from string (removes currency symbol)
 */
export function parseCurrencyValue(value: string | number): number {
  if (typeof value === 'number') {
    return value;
  }
  if (!value) {
    return 0;
  }
  // Remove currency symbols and commas
  const cleaned = value.replace(/[$₹,]/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Convert currency using API
 * Note: This is async and should be used with await or in async functions
 */
export async function convertCurrency(
  value: number,
  from: Currency,
  to: Currency
): Promise<number> {
  if (from === to) {
    return value;
  }

  try {
    const { exchangeRatesApi } = await import('../api/exchangeRates');
    return await exchangeRatesApi.convert(value, from, to);
  } catch (error) {
    console.error('Error converting currency, using fallback rate:', error);
    // Fallback to approximate rate: 1 INR = 0.012 USD (or 1 USD = 83.33 INR)
    const fallbackRate = 83.33;
    if (from === 'USD' && to === 'INR') {
      return value * fallbackRate;
    } else if (from === 'INR' && to === 'USD') {
      return value * 0.012; // 1 INR = 0.012 USD
    }
    return value;
  }
}
